import CardCompanyHeadcountHistory from "@/components/company/card-company-headcount-history";
import CardWorkforceBreakdown from "@/components/company/card-workforce-breakdown";
import CompanySectionCard, {
  CompanyCardSkeleton,
} from "@/components/company/company-section-card";
import { FadeInSection } from "@/components/company/company-animations";
import DataTableCompanyEmployees from "@/components/data-table-company-employees/data-table-company-employees";
import { CompanyErrorMessage } from "@/components/company/company-run-alerts";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useEmployeeSearch } from "@/hooks/useEmployeeSearch";
import { useTranslation } from "@/hooks/useTranslation";
import { COMPANY_CARD_SECTIONS } from "@/lib/company-card-sections";
import { hasHeadcountHistoryData } from "@/lib/company-events";
import {
  BadgeInfoIcon,
  ExternalLinkIcon,
  Loader2Icon,
  SearchIcon,
  SparklesIcon,
} from "lucide-react";
import { RiLinkedinFill } from "react-icons/ri";
import { Link, Navigate } from "react-router-dom";
import { CompanyDTO } from "@shaarp/common";
import { useCompanyTabContext } from "./company-tab-context";

/** The people of a company; a team whose direct contact is cut is sent back to insights (#757). */
export default function CompanyEmployeesPage() {
  const arePeopleVisible = useArePeopleVisible();

  if (!arePeopleVisible) {
    return <Navigate to="../insights" replace />;
  }

  return <CompanyEmployeesSearch />;
}

/** Who works there: the workforce's shape and curve. */
function CompanyWorkforce({ company }: { company: CompanyDTO }) {
  return (
    <div className="flex flex-col gap-4">
      <CardWorkforceBreakdown company={company} />
      <CompanySectionCard
        sections={COMPANY_CARD_SECTIONS.headcountHistory}
        hasData={hasHeadcountHistoryData(company)}
        skeleton={<CompanyCardSkeleton />}
      >
        <CardCompanyHeadcountHistory company={company} />
      </CompanySectionCard>
    </div>
  );
}

function CompanyEmployeesSearch() {
  const { t } = useTranslation();
  const { company } = useCompanyTabContext();
  const search = useEmployeeSearch({ company });

  if (search.error) {
    return <CompanyErrorMessage error={search.error} />;
  }

  if (search.isSmallCompany) {
    return (
      <FadeInSection>
        <div className="flex flex-col gap-4">
          <DataTableCompanyEmployees
            employees={search.employees}
            companyId={company.id}
            isLoading={search.isSearching || search.isSearchQueryLoading}
          />
          <CompanyWorkforce company={company} />
        </div>
      </FadeInSection>
    );
  }

  return (
    <FadeInSection>
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="shrink-0 rounded-full flex items-center justify-center w-10 h-10 bg-muted">
                <SearchIcon className="w-5 h-5" />
              </div>
              {t("company.employees.searchQuery.title")}
            </CardTitle>
            <CardDescription>
              {t("company.employees.searchQuery.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {search.isSearchQueryLoading ? (
              <Skeleton className="w-full h-10" />
            ) : (
              <>
                <div className="flex gap-2">
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={search.handleGenerateQuery}
                          disabled={
                            search.isGeneratingQuery ||
                            search.isSearching ||
                            search.isSearchQueryLoading
                          }
                        >
                          {search.isGeneratingQuery ? (
                            <Loader2Icon className="w-4 h-4 animate-spin" />
                          ) : (
                            <SparklesIcon className="w-4 h-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {t("company.employees.searchQuery.generateTarget")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    value={search.searchQuery}
                    onChange={(e) => search.setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !search.isButtonDisabled()
                      ) {
                        e.preventDefault();
                        search.handleButtonClick();
                      }
                    }}
                    placeholder={t("company.employees.searchQuery.placeholder")}
                    className={
                      search.hasSearched && !search.queryHasChanged
                        ? "flex-1"
                        : "flex-1 border border-warning rounded-md transition-all duration-300"
                    }
                  />

                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className="inline-block">
                          <Button
                            onClick={search.handleButtonClick}
                            disabled={search.isButtonDisabled()}
                            className="pointer-events-auto"
                          >
                            {search.isSearching ? (
                              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <SearchIcon className="w-4 h-4 mr-2" />
                            )}
                            {search.getButtonText()}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {search.getDisabledTooltip() && (
                        <TooltipContent>
                          <p>{search.getDisabledTooltip()}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </>
            )}

            {!search.hasSearched && (
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                <p className="text-sm">
                  {t("company.employees.searchQuery.emptyState.description")}
                </p>
              </div>
            )}

            {search.hasSearched && !search.isSearching && search.employees.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground gap-3">
                <p className="text-lg font-medium">
                  {t("company.employees.searchQuery.noResults.title")}
                </p>
                <p className="text-sm">
                  {t("company.employees.searchQuery.noResults.description")}
                </p>
                <p className="text-xs">
                  <BadgeInfoIcon className="w-3.5 h-3.5 inline mr-1" />
                  {t("company.employees.notAllEmployeesAreShown.gdpr")}
                </p>
                <div className="flex gap-2 mt-1">
                  {company.socialNetworks.linkedin.url && (
                    <Button
                      variant="default"
                      size="sm"
                      asChild
                    >
                      <Link
                        to={`${company.socialNetworks.linkedin.url}/people`}
                        target="_blank"
                      >
                        <RiLinkedinFill className="w-4 h-4" />
                        {t("company.employees.viewOnLinkedIn")}
                        <ExternalLinkIcon className="w-3.5 h-3.5" />
                      </Link>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={search.handleGenerateAndSearch}
                    disabled={search.isGeneratingQuery || search.isSearching}
                  >
                    {search.isGeneratingQuery || search.isSearching ? (
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <SparklesIcon className="w-4 h-4 mr-2" />
                    )}
                    {t(
                      "company.employees.searchQuery.searchOtherRelevantContacts",
                    )}
                  </Button>
                </div>
              </div>
            )}

            {search.hasSearched && (search.isSearching || search.employees.length > 0) && (
              <div className="flex items-center gap-2">
                <p className="flex-1 text-xs text-muted-foreground">
                  <BadgeInfoIcon className="w-3.5 h-3.5 inline mr-1" />
                  <span
                    className={
                      search.targetMatch === "perfect" ? "" : "underline"
                    }
                  >
                    {(() => {
                      switch (search.targetMatch ?? "other") {
                        case "perfect":
                          return t(
                            "company.employees.notAllEmployeesAreShown.perfect",
                          );
                        case "management":
                          return t(
                            "company.employees.notAllEmployeesAreShown.management",
                          );
                        case "other":
                        default:
                          return t(
                            "company.employees.notAllEmployeesAreShown.other",
                          );
                      }
                    })()}
                  </span>{" "}
                  {t("company.employees.notAllEmployeesAreShown.gdpr")}
                </p>
                {company.socialNetworks.linkedin.url && (
                  <Button
                    variant="secondary"
                    size="sm"
                    asChild
                    className="shrink-0"
                  >
                    <Link
                      to={`${company.socialNetworks.linkedin.url}/people`}
                      target="_blank"
                    >
                      <RiLinkedinFill className="w-4 h-4" />
                      {t("company.employees.viewOnLinkedIn")}
                      <ExternalLinkIcon className="w-3.5 h-3.5" />
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {search.hasSearched && (search.isSearching || search.employees.length > 0) && (
          <>
            <DataTableCompanyEmployees
              employees={search.employees}
              companyId={company.id}
              isLoading={search.isSearching}
            />
            {search.employees.length > 5 && (
              <div className="flex flex-col items-center gap-2">
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <span className="inline-block">
                        <Button
                          onClick={search.handleButtonClick}
                          disabled={search.isButtonDisabled()}
                          className="pointer-events-auto"
                        >
                          {search.isSearching ? (
                            <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <SearchIcon className="w-4 h-4 mr-2" />
                          )}
                          {search.getButtonText()}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {search.getDisabledTooltip() && (
                      <TooltipContent>
                        <p>{search.getDisabledTooltip()}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {search.currentPage >= 1 &&
                  !search.hasMoreResults &&
                  !search.queryHasChanged &&
                  !search.isSearching && (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "company.employees.searchQuery.noAdditionalResults",
                      )}
                    </p>
                  )}
              </div>
            )}
          </>
        )}

        {search.maxPagesReached && (
          <div className="flex flex-col items-center justify-center text-center text-muted-foreground">
            <p className="text-md font-medium">
              {t("company.employees.searchQuery.refineSearch.title")}
            </p>
            <p className="text-sm">
              {t("company.employees.searchQuery.refineSearch.description")}
            </p>
          </div>
        )}

        <CompanyWorkforce company={company} />
      </div>
    </FadeInSection>
  );
}
